import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import ProductForm from '../../components/forms/ProductForm';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ProductRegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);

  // Capturar dados da calculadora se existirem
  const calculatorData = location.state?.calculatorData;
  
  // Mapear categorias da calculadora para categorias do formulário
  const mapCalculatorCategory = (calcCategory) => {
    const categoryMap = {
      'salgados': 'Salgados',
      'doces': 'Doces', 
      'bebidas': 'Bebidas',
      'sanduiches': 'Lanches',
      'frutas': 'Snacks',
      'outros': 'Outros'
    };
    return categoryMap[calcCategory] || 'Outros';
  };

  // Preparar dados iniciais do formulário com dados da calculadora
  const initialFormData = calculatorData ? {
    name: calculatorData.name,
    category: mapCalculatorCategory(calculatorData.category),
    unit: calculatorData.unit,
    cost: calculatorData.cost_price.toString(),
    price: calculatorData.sale_price.toFixed(2),
    stock_quantity: '',
    min_stock: '',
    description: calculatorData.calculation_data && calculatorData.profit_margin ? 
      `Produto calculado com margem de ${calculatorData.profit_margin.toFixed(1)}%` : 
      '',
    barcode: '' // Garantir que código de barras inicie vazio
  } : null;

  const handleSubmit = async (productData) => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setLoading(true);
      
      // Preparar dados do produto
      const newProduct = {
        ...productData,
        company_id: company.id,
        active: productData.active !== false, // Default true
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Creating product:', newProduct);

      // Criar produto usando dataHelpers
      const createdProduct = await dataHelpers.createProduct(company.id, newProduct);
      
      console.log('Product created successfully:', createdProduct);
      
      toast.success('Produto cadastrado com sucesso!');
      
      // Redirecionar para a lista de produtos ou dashboard
      navigate('/dashboard/products', { 
        state: { 
          message: 'Produto cadastrado com sucesso!',
          productId: createdProduct.id 
        }
      });
      
    } catch (error) {
      console.error('Error creating product:', error);
      
      let errorMessage = 'Erro ao cadastrar produto';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe um produto com este nome ou código de barras';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para cadastrar produtos';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/products');
  };

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/products')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cadastrar Produto</h1>
              <p className="text-gray-600">
                Adicione um novo produto ao seu catálogo
              </p>
            </div>
          </div>
        </div>

        {/* Informações da empresa */}
        {company && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Cadastrando produto para: <strong>{company.name}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Informação sobre dados da calculadora */}
        {calculatorData && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800">
                <strong>Dados da Calculadora:</strong> Os campos foram preenchidos automaticamente com os valores calculados. 
                Você pode ajustá-los conforme necessário.
              </span>
            </div>
          </div>
        )}

        {/* Formulário */}
        <ProductForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
          initialData={initialFormData}
        />

        {/* Dicas */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Dicas para cadastro</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Nome do Produto</h4>
              <ul className="space-y-1">
                <li>• Use nomes descritivos e claros</li>
                <li>• Evite abreviações confusas</li>
                <li>• Inclua características importantes</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Preços</h4>
              <ul className="space-y-1">
                <li>• Defina um preço competitivo</li>
                <li>• Considere o custo para calcular margem</li>
                <li>• Monitore a concorrência</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Estoque</h4>
              <ul className="space-y-1">
                <li>• Configure estoque mínimo adequado</li>
                <li>• Monitore produtos em falta</li>
                <li>• Atualize regularmente as quantidades</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Categorias</h4>
              <ul className="space-y-1">
                <li>• Organize produtos por tipo</li>
                <li>• Facilita a busca no PDV</li>
                <li>• Melhora relatórios de vendas</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    
  );
};

export default ProductRegisterPage;
